/**
 * The preview's per-kind renderers (2026-08-02), drawn by BOTH of today's shells — the modal overlay
 * (FilePreviewOverlay) and the floating multitask window (FloatingPreviewWindow) — one drawing, two shells.
 *
 * They only draw: what a preview holds (where the file is, its bytes shaped for its kind, whether it degraded,
 * its download and its retry) is src/hooks/filePreview/usePreview.ts, and the image viewer's zoom, turn and pan
 * are useImageViewer (PV5-8:C part 1, 2026-10-13), so the new design's preview draws from the same answers.
 */
import React from 'react';
import styled from 'styled-components';
import { previewKindLabel } from './previewKind';
import { htmlLooksIncomplete, htmlNeedsScripts, injectRuntimeGuard } from './sceneRuntimeGuard';
import {
  formatSize,
  inlinePdfSupported,
  isVectorImage,
  openableHref,
  openInTab,
  type PreviewState,
} from '../../hooks/filePreview/usePreview';
import { useImageViewer } from '../../hooks/filePreview/useImageViewer';

export const ToolButton = styled.button`
  background: rgba(255, 255, 255, 0.07); color: #dbe4f2;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px; padding: 6px 10px;
  font-size: 12px; cursor: pointer; flex: 0 0 auto;
  &:hover { background: rgba(255, 255, 255, 0.14); }

  /* Touch: the 27px desktop pill is under every platform's target floor. */
  @media (pointer: coarse) {
    padding: 10px 14px;
    font-size: 13px;
    min-height: 40px;
  }
`;

export const FullFrame = styled.iframe`
  border: 0; width: 100%; height: 100%; background: #fff;
`;

const TextView = styled.pre<{ $wrap: boolean }>`
  width: 100%; height: 100%; margin: 0; padding: 16px;
  overflow: auto; font-size: 12.5px; line-height: 1.5;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #dbe4f2; background: #0a0f1a;
  white-space: ${(p) => (p.$wrap ? 'pre-wrap' : 'pre')};
  word-break: ${(p) => (p.$wrap ? 'break-word' : 'normal')};
`;

const CenterCard = styled.div`
  text-align: center; padding: 32px;
  /* Viewport-bounded (mobile UX 2026-08-14): a flat 420px cap overflowed a
     390px phone before padding was even counted. */
  max-width: min(420px, calc(100vw - 48px));
  display: flex; flex-direction: column; gap: 12px; align-items: center;
  color: #b9c4d6; font-size: 14px; margin: auto;
`;

/* R13 (2026-08-13 truncated-deliverable incident): shell for an HTML file
 * that opens like a full document but never reaches </html> — the cut-
 * generation signature that used to render as a silent black rectangle. */
const FrameColumn = styled.div`
  width: 100%; height: 100%; display: flex; flex-direction: column; min-height: 0;
`;

const IncompleteBanner = styled.div`
  flex: 0 0 auto; padding: 8px 14px; font-size: 12.5px; line-height: 1.45;
  color: #ffd9a0; background: rgba(120, 78, 0, 0.35);
  border-bottom: 1px solid rgba(255, 200, 120, 0.35);
`;

const ImageStage = styled.div`
  width: 100%; height: 100%; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  cursor: grab;
  &:active { cursor: grabbing; }
`;

export const ImageRenderer: React.FC<{
  src: string;
  alt: string;
  vector?: boolean;
  /**
   * What to show when the browser cannot DECODE this image (2026-09-12).
   *
   * `resolvePreviewKind` classifies every `image/*` as 'image', which is right
   * for the tile's purposes and right on the platform where the format is
   * native — an iPhone photo is HEIC, and Safari paints it perfectly. Chrome
   * and Windows cannot, and until now this renderer answered that by showing a
   * broken <img> under a zoom/rotate/reset toolbar that did nothing: the
   * overlay promising a preview it could not paint, while the tile beside it
   * had already honestly fallen back to a file card.
   *
   * Deliberately a decode-time fallback rather than a classifier change: the
   * classifier is pure (name + type in), it cannot know which codecs the
   * running browser has, and demoting HEIC to 'binary' everywhere would take
   * the working preview away from the one platform where it works.
   */
  fallback?: React.ReactNode;
}> = ({ src, alt, vector = false, fallback }) => {
  const v = useImageViewer(src);
  if (v.failed && fallback) return <>{fallback}</>;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ImageStage
        onWheel={v.onWheel}
        onMouseDown={v.onMouseDown}
        onMouseMove={v.onMouseMove}
        onMouseUp={v.onMouseUp}
        onMouseLeave={v.onMouseLeave}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          onError={v.onImageError}
          data-preview-image=""
          style={{
            maxWidth: '100%', maxHeight: '100%',
            ...(vector ? { width: '100%', height: '100%', objectFit: 'contain' as const } : {}),
            transform: v.transform,
            transition: v.dragging ? 'none' : 'transform 120ms ease',
          }}
        />
      </ImageStage>
      <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8 }}>
        <ToolButton onClick={v.zoomIn} aria-label="zoom in">＋</ToolButton>
        <ToolButton onClick={v.zoomOut} aria-label="zoom out">－</ToolButton>
        <ToolButton onClick={v.rotate} aria-label="rotate">⟳ 90°</ToolButton>
        <ToolButton onClick={v.reset} aria-label="reset view">Reset</ToolButton>
      </div>
    </div>
  );
};

/** The kind-dispatched body — identical in the modal and the floating window. */
export const PreviewBody: React.FC<{ p: PreviewState; wrap: boolean }> = ({ p, wrap }) => {
  if (p.degraded || p.kind === 'binary') {
    return (
      <CenterCard>
        <div style={{ fontSize: 40 }}>📄</div>
        <div style={{ wordBreak: 'break-word' }}><strong>{p.name}</strong></div>
        <div>{previewKindLabel(p.kind)}{p.size ? ` · ${formatSize(p.size)}` : ''}</div>
        <div style={{ fontSize: 12, color: '#8896ab' }}>
          {p.degraded && p.kind !== 'binary'
            ? 'The preview didn’t load — the file may still be uploading. Try again in a moment, or download it directly.'
            : 'This file type opens in its own application.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {p.degraded && p.kind !== 'binary' && <ToolButton onClick={p.retry}>Try again</ToolButton>}
          {(p.url || p.blobUrl) && <ToolButton onClick={p.download}>Download</ToolButton>}
        </div>
      </CenterCard>
    );
  }
  switch (p.kind) {
    case 'image': {
      if (!p.url) return null;
      // The same shape the PDF branch already uses when a device cannot render
      // inline: the app's own card with real actions, never a broken frame.
      // Reached only if the <img> actually fails to decode, so a browser that
      // CAN paint the format (Safari with an iPhone's HEIC) is unaffected.
      const openHref = openableHref(p);
      const undisplayable = (
        <CenterCard>
          <div style={{ fontSize: 40 }}>🖼️</div>
          <div style={{ wordBreak: 'break-word' }}><strong>{p.name}</strong></div>
          <div>{previewKindLabel(p.kind)}{p.size ? ` · ${formatSize(p.size)}` : ''}</div>
          <div style={{ fontSize: 12, color: '#8896ab' }}>
            This browser can’t display this image format — open it in your photo
            viewer or save it.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            {openHref && (
              <ToolButton onClick={() => openInTab(openHref)}>
                Open image
              </ToolButton>
            )}
            <ToolButton onClick={p.download}>Download</ToolButton>
          </div>
        </CenterCard>
      );
      return (
        <ImageRenderer
          src={p.url}
          alt={p.name}
          vector={isVectorImage(p.name, p.type)}
          fallback={undisplayable}
        />
      );
    }
    case 'video':
      return p.url ? (
        // 2026-09-08: preload only the index (the moov box the platform now moves first) and never
        // let iOS Safari hijack the page into its own fullscreen player.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video src={p.url} controls playsInline preload="metadata" data-preview-video="" style={{ maxWidth: '100%', maxHeight: '100%', margin: 'auto' }} />
      ) : null;
    case 'audio':
      return p.url ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio src={p.url} controls preload="metadata" data-preview-audio="" style={{ width: 'min(560px, 90%)', margin: 'auto' }} />
      ) : null;
    case 'pdf': {
      if (!inlinePdfSupported()) {
        // The app's own card — real filename, real actions — instead of the
        // browser's broken iframe rendering. Checked BEFORE the blobUrl
        // gate: the card doesn't need the PDF's bytes, so it must not wait
        // for (or stall on) the full download the blob lane performs
        // (review 2026-08-14) — it renders as soon as ANY href exists.
        if (!p.url && !p.blobUrl) return <CenterCard>Loading PDF…</CenterCard>;
        const openHref = openableHref(p);
        return (
          <CenterCard>
            <div style={{ fontSize: 40 }}>📄</div>
            <div style={{ wordBreak: 'break-word' }}><strong>{p.name}</strong></div>
            <div>{previewKindLabel(p.kind)}{p.size ? ` · ${formatSize(p.size)}` : ''}</div>
            <div style={{ fontSize: 12, color: '#8896ab' }}>
              This device doesn’t display PDFs inside the page — open it in your viewer or save it.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              {openHref && (
                <ToolButton onClick={() => openInTab(openHref)}>
                  Open PDF
                </ToolButton>
              )}
              <ToolButton onClick={p.download}>Download</ToolButton>
            </div>
          </CenterCard>
        );
      }
      return p.blobUrl ? <FullFrame title={p.name} src={p.blobUrl} /> : <CenterCard>Loading PDF…</CenterCard>;
    }
    case 'docx':
      return p.convertedHtml != null
        ? <FullFrame title={p.name} sandbox="" srcDoc={p.convertedHtml} />
        : <CenterCard>Loading document…</CenterCard>;
    case 'sheet':
      return p.convertedHtml != null
        ? <FullFrame title={p.name} sandbox="" srcDoc={p.convertedHtml} />
        : <CenterCard>Loading spreadsheet…</CenterCard>;
    case 'scene3d': {
      // Guard injected (2026-08-09 black-preview report): a scene that dies
      // at boot — blocked CDN import, module resolution error, no WebGL —
      // now explains itself in plain language instead of sitting black.
      if (p.text == null) return <CenterCard>Loading 3D scene…</CenterCard>;
      const sceneFrame = <FullFrame title={p.name} sandbox="allow-scripts allow-pointer-lock" allow="pointer-lock *; fullscreen *" srcDoc={injectRuntimeGuard(p.text)} />;
      return htmlLooksIncomplete(p.text)
        ? <FrameColumn><IncompleteBanner>⚠️ This file appears to be incomplete — the generation may have been cut off before it finished. Ask for it to be regenerated.</IncompleteBanner>{sceneFrame}</FrameColumn>
        : sceneFrame;
    }
    case 'html': {
      // 2026-08-09 black-preview report: an interactive HTML app rendered
      // with scripts stripped paints its static background and nothing else
      // — a dead black frame with working buttons around it. Scripted HTML
      // now runs under the SAME guarantee the scene3d lane has always shipped
      // with: `allow-scripts` in an opaque origin (no allow-same-origin, so
      // the content can never touch the app origin, cookies, or storage),
      // plus the runtime guard so a boot failure still explains itself.
      // Script-free HTML keeps the fully inert frame.
      if (p.text == null) return <CenterCard>Loading…</CenterCard>;
      const htmlFrame = htmlNeedsScripts(p.text)
        ? <FullFrame title={p.name} sandbox="allow-scripts allow-pointer-lock" allow="pointer-lock *; fullscreen *" srcDoc={injectRuntimeGuard(p.text)} />
        : <FullFrame title={p.name} sandbox="" srcDoc={p.text} />;
      // R13 (2026-08-13 datacenter-hvac incident): a cut-off head fragment
      // opens like a page and renders black — say so above the frame.
      return htmlLooksIncomplete(p.text)
        ? <FrameColumn><IncompleteBanner>⚠️ This file appears to be incomplete — the generation may have been cut off before it finished. Ask for it to be regenerated.</IncompleteBanner>{htmlFrame}</FrameColumn>
        : htmlFrame;
    }
    case 'text':
      return p.text != null
        ? <TextView $wrap={wrap}>{p.text}</TextView>
        : <CenterCard>Loading…</CenterCard>;
    default:
      return null;
  }
};
